// FundManagerDetail.jsx
import React, { useState, useEffect } from 'react';
import { 
    Building2, 
    Globe, 
    MapPin, 
    Calendar,
    DollarSign,
    BarChart3,
    FileText
} from 'lucide-react';
import { useParams } from "react-router";
import './FundManagerDetailPage.css';
import { formatCurrency, formatDateIntl,formatInterest } from "../../util/currency_lookup";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader } from '../../components/loader';

const FundManagerDetailPage = () => {
  const [fundManager, setFundManager] = useState(null);
  const [moneyMarketFunds, setMoneyMarketFunds] = useState([]);
  const [investmentData, setInvestmentData] = useState([]);
  const [loading, setLoading] = useState({
    fundManager: true,
    moneyMarketFunds: true,
    investmentMetrics: true,
    summarySummary: true,
  });
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('month');
  const [summaryFunds, setSummaryFunds] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    fetchFundManager();
    fetchMoneyMarketFunds();
    fetchInvestmentData(timeframe);
    fetchSummary();
  }, [id]);

  // useEffect(() => {
  //   fetchInvestmentData(timeframe);
  // }, [timeframe]);

  const fetchFundManager = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/fund-manager-by/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch fund manager');
      }

      const data = await response.json();
      setFundManager(data);
    } catch (err) {
      setError(err.message);
    }  finally {
      setLoading(prev => ({ ...prev, fundManager: false }));
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/fund-manager/investments/${id}/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch money market funds');
      }

      const data = await response.json();
      console.log(data);
      setSummaryFunds(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, summarySummary: false }));
    }
  };

  const fetchMoneyMarketFunds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/funds/${id}/money-market-funds`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch money market funds');
      }

      const data = await response.json();
      setMoneyMarketFunds(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, moneyMarketFunds: false }));
    }
  };

  const fetchInvestmentData = async (timeframe) => {
    try {
      const token = localStorage.getItem('token');

      let url = `${process.env.REACT_APP_API_URL}/api/v2/fund-manager/investments/${id}/overtime`;
      if (timeframe) {
        url += `?&timeframe=${encodeURIComponent(timeframe)}`;
      }
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch investment data');
      }

      const data = await response.json();
      setInvestmentData(data);
    } catch (err) {
      setError(err.message);
    }  finally {
      setLoading(prev => ({ ...prev, investmentMetrics: false }));
    }
  };

  const isLoading = Object.values(loading).some(status => status);

  if (isLoading) {
    return <Loader loading={true} />;
  }

  if (error) return <div className="error">{error}</div>;
  if (!fundManager) return <div className="error">No fund manager found</div>;

  return (
    <div className="fund-manager-detail">
      <div className="header">
        <div className="header-content">
          <h1>Fund Manager Profile</h1>
        </div>
        <div className="fund-manager-id">#{fundManager.id}</div>
      </div>

      {/* Basic Information */}
      <div className="overview">
        <div className="overview-card">
          <div className="overview-title">Basic Information</div>
          <div className="details-grid">
            <div className="detail-card">
              <div className="detail-card-content">
                <Building2 className="detail-icon" />
                <div>
                  <div className="detail-label">Name</div>
                  <div className="detail-value">{fundManager.name}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <MapPin className="detail-icon" />
                <div>
                  <div className="detail-label">Address</div>
                  <div className="detail-value">{fundManager.address}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Globe className="detail-icon" />
                <div>
                  <div className="detail-label">Website</div>
                  <div className="detail-value">
                    <a href={fundManager.website} target="_blank" rel="noopener noreferrer">
                      {fundManager.website}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Calendar className="detail-icon" />
                <div>
                  <div className="detail-label">Created At</div>
                  <div className="detail-value">
                    {formatDateIntl(fundManager.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Statistics */}
      <div className="statistics-section">
        <h2>Investment Statistics</h2>
        <div className="statss-grid">
          <div className="stat-card">
            <DollarSign className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{formatCurrency(summaryFunds.total_net_investment)}</div>
              <div className="stat-label">Total Invested Amount</div>
            </div>
          </div>
          <div className="stat-card">
            <FileText className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{summaryFunds.total_transactions}</div>
              <div className="stat-label">Total Transactions</div>
            </div>
          </div>
          <div className="stat-card">
            <FileText className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{summaryFunds.average_investment_size}</div>
              <div className="stat-label">Average Invested Amount</div>
            </div>
          </div>
          <div className="stat-card">
            <FileText className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{summaryFunds.largest_investment}</div>
              <div className="stat-label">Largest Investment</div>
            </div>
          </div>
        </div>

        {/* Investment Chart */}
        <div className="chart-section">
          <h3>Investment Over Time</h3>
          <div className="timeframe-selector">
            <select 
              value={timeframe} 
              onChange={(e) => setTimeframe(e.target.value)}
              className="timeframe-select"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={investmentData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => formatDateIntl(date, 'short')}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value, 'USD', true)}
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value, 'USD')}
                labelFormatter={(date) => formatDateIntl(date)}
              />
              <Bar dataKey="new_investments" fill="#4ade80" name="New Investments" />
              <Bar dataKey="redemptions" fill="#f87171" name="Redemptions" />
              <Line 
                type="monotone" 
                dataKey="total_investment" 
                stroke="#6366f1" 
                name="Net Flow"
              />
            </ComposedChart>
        </ResponsiveContainer>
        </div>
      </div>

      {/* Money Market Funds Table */}
      <div className="money-market-funds-section">
        <h2>Money Market Funds</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Fund Name</th>
                <th>Symbol</th>
                <th>Returns</th>
                {/* <th>YTD Return</th>
                <th>Status</th> */}
              </tr>
            </thead>
            <tbody>
              {moneyMarketFunds.map((fund) => (
                <tr key={fund.fund_id}>
                  <td>{fund.name}</td>
                  <td>{fund.symbol}</td>
                  <td>{formatInterest(fund.interest_rate)}</td>
                  {/* <td>{formatCurrency(fund.nav)}</td>
                  <td>{fund.ytd_return}%</td> */}
                  <td>
                    {/* <span className={`status ${fund.status.toLowerCase()}`}>
                      {fund.status}
                    </span> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FundManagerDetailPage;
